import React, { useState, useEffect } from 'react'; 
import { User } from '@common/auth/user';
import { FullscreenIcon } from '@ui/icons/material/Fullscreen';
import { FullscreenExitIcon } from '@ui/icons/material/FullscreenExit';
import { FaceIcon } from '@ui/icons/material/Face';
import { FaceRetouchingOffIcon } from '@ui/icons/material/FaceRetouchingOff';
import { CloseIcon } from '@ui/icons/material/Close';
import { ShareIcon } from '@ui/icons/material/Share';
import { ChatIcon } from '@ui/icons/material/Chat';
import { WhatsappIcon } from '@ui/icons/social/whatsapp';
import { InstagramIcon } from '@ui/icons/social/instagram';
import { FacebookIcon } from '@ui/icons/social/facebook';
import { TelegramIcon } from '@ui/icons/social/telegram';
import { TwitterIcon } from '@ui/icons/social/twitter';
import { LiveTvIcon } from '@ui/icons/material/LiveTv'; 

import { useAuth } from '@common/auth/use-auth'; // Importe o hook de autenticação
import { useNavigate } from 'react-router-dom'; // Importe o hook para navegação


declare global {
  interface Window {
    MSStream?: any;
  }
}


export function ArtistLivePanel({ artist }: { artist: User }) {
  const [userCounts, setUserCounts] = useState<{ onlineCount: number; offlineCount: number } | null>(null);
  const [showLiveStream, setShowLiveStream] = useState(false);
  const [liveStreamUrl, setLiveStreamUrl] = useState('');
  const [isArtistLoggedIn, setIsArtistLoggedIn] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [artistUsername, setArtistUsername] = useState('');
  const [showSharePopup, setShowSharePopup] = useState(false);
  const [showChatPopup, setShowChatPopup] = useState(false);

  const [artistStatusLive, setArtistStatusLive] = useState<'Ativa' | 'Inativa'>('Inativa');

  const [isMobile, setIsMobile] = useState(false);
  

  // Captura o ID do artista da URL
  const artistId = window.location.pathname.split('/')[2]; // Obtém o ID do artista da URL


  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;

 
  const { user } = useAuth(); // Verifica se o usuário está autenticado
  const navigate = useNavigate(); // Hook para navegação

  
  // Verifica a autenticação e redireciona se o usuário não estiver logado
  useEffect(() => {
    if (!user) {
      navigate('/login'); // Redireciona para a página de login
    }
  }, [user, navigate]);
  

  useEffect(() => {
    const fetchLiveStreamUrl = async () => {
      try {
        const path = window.location.pathname;
        const urlParts = path.split('/');
        const artistUserName = urlParts[urlParts.length - 1];

        console.log(`Nome do Artista: ${artistUserName}`);
        setArtistUsername(artistUserName);

        // Buscar o status do artista
        const statusResponse = await fetch(`https://apicontador.sintonizy.com/status_live_artist.php?artist_name=${artistUserName}`);
        const statusData = await statusResponse.json();
        console.log('Status do artista:', statusData.status_live); // Adicione este log
        setArtistStatusLive(statusData.status_live);

        const loggedInUserIdResponse = await fetch('https://vibe.sintonizy.com/api/v1/users/me', {
          headers: {
            Authorization: 'Bearer 1|vRqh3zQF5hC69jECxE1XI4o3iHED33cIQ9glQIlYe755d403',
          },
        });

        if (!loggedInUserIdResponse.ok) throw new Error('Erro na API do usuário');

        const loggedInUserData = await loggedInUserIdResponse.json();
        const loggedInUserName = loggedInUserData.user.username || loggedInUserData.user.display_name;

        console.log(`Usuário logado: ${loggedInUserName}`);
        setIsArtistLoggedIn(artistUserName === loggedInUserName);

        if (artistUserName === loggedInUserName) {
          setLiveStreamUrl(`https://weblive.sintonizy.com/broadcast?id=${artistUserName}&name=live`);
        } else {
          setLiveStreamUrl(`https://weblive.sintonizy.com/viewer?id=${artistUserName}&name=${loggedInUserName}`);
        }

        await fetch(
          `https://apicontador.sintonizy.com/track_users.php?url=${encodeURIComponent(window.location.href)}&artist=${encodeURIComponent(
            artistUserName
          )}&username=${encodeURIComponent(loggedInUserName)}`
        );
      } catch (error) {
        console.error('Erro ao abrir o stream ao vivo.', error);
      }
    };

    fetchLiveStreamUrl();
  }, []);

  useEffect(() => {
    const fetchUserCounts = async () => {
      try {
        if (!artistUsername) return;

        const response = await fetch(
          `https://apicontador.sintonizy.com/track_users.php?url=${encodeURIComponent(window.location.href)}&artist=${encodeURIComponent(
            artistUsername
          )}`
        );

        if (!response.ok) throw new Error('Erro na resposta da API');

        const data = JSON.parse(await response.text());
        const onlineCount = Number(data.onlineCount);
        const offlineCount = Number(data.offlineCount);

        if (!isNaN(onlineCount) && !isNaN(offlineCount)) {
          setUserCounts({ onlineCount, offlineCount });
        }
      } catch (error) {
        console.error('Erro ao obter a contagem de usuários.', error);
      }
    };

    fetchUserCounts();
    const interval = setInterval(fetchUserCounts, 5000);

    return () => clearInterval(interval);
  }, [artistUsername]);

  const updateUserActivity = async () => {
    try {
      const path = window.location.pathname;
      const urlParts = path.split('/');
      const artistUserName = urlParts[urlParts.length - 1];

      const loggedInUserIdResponse = await fetch('https://vibe.sintonizy.com/api/v1/users/me', {
        headers: {
          Authorization: 'Bearer 1|vRqh3zQF5hC69jECxE1XI4o3iHED33cIQ9glQIlYe755d403',
        },
      });

      if (!loggedInUserIdResponse.ok) throw new Error('Erro na API do usuário');

      const loggedInUserData = await loggedInUserIdResponse.json();
      const loggedInUserName = loggedInUserData.user.username || loggedInUserData.user.display_name;

      await fetch(
        `https://apicontador.sintonizy.com/track_users.php?url=${encodeURIComponent(window.location.href)}&artist=${encodeURIComponent(
          artistUserName
        )}&username=${encodeURIComponent(loggedInUserName)}`
      );
    } catch (error) {
      console.error('Erro ao atualizar a atividade do usuário.', error);
    }
  };

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') updateUserActivity();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  const toggleFullscreen = () => {
    if (isIOS) {
      if (!isFullscreen) {
        document.documentElement.style.overflow = 'hidden';
        document.documentElement.style.width = '100vw';
        document.documentElement.style.height = '100vh';
      } else {
        document.documentElement.style.overflow = '';
        document.documentElement.style.width = '';
        document.documentElement.style.height = '';
      }
    } else {
      if (!isFullscreen) {
        document.documentElement.requestFullscreen();
      } else {
        document.exitFullscreen();
      }
    }
    setIsFullscreen(!isFullscreen);
  };

  const closeViewerLiveStream = () => {
    // Função que fecha o live stream no lado do telespectador
    console.log('Fechando live no lado do telespectador');
    setShowLiveStream(false);
  };

  const closeLiveStream = async () => {
    try {
      console.log('Encerrando live para o artista:', artistUsername);
  
      // Enviar requisição para atualizar o status_live para "Inativa"
      const response = await fetch('https://apicontador.sintonizy.com/status_live_artist.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          status_live: 'Inativa',
          artist_name: artistUsername,
          data_hora_ultima_live: new Date().toISOString(),
        }),
      });
  
      const responseText = await response.text();
      console.log('Resposta do servidor:', responseText);
  
      let data;
      try {
        data = JSON.parse(responseText);
      } catch (parseError) {
        console.error('Erro ao analisar a resposta JSON:', parseError);
        alert(`Erro na resposta do servidor. Resposta recebida: ${responseText}`);
        return;
      }
  
      if (!response.ok) {
        console.error('Resposta do servidor não ok:', response.status, data);
        throw new Error(`HTTP error! status: ${response.status}`);
      }
  
      console.log('Live encerrada com sucesso:', data);
  
      // Fechar a live na interface
      setShowLiveStream(false);
    } catch (error) {
      console.error('Erro ao encerrar a live:', error);
      if (error instanceof Error) {
        alert(`Erro ao encerrar a live: ${error.message}`);
      } else {
        alert('Erro desconhecido ao encerrar a live');
      }
    }
  };
  
  
  

  const toggleSharePopup = () => {
    setShowSharePopup(!showSharePopup);
  };

  const toggleChatPopup = () => {
    setShowChatPopup(!showChatPopup);
  };

  const startLive = async () => {
    try {
      console.log('Iniciando live para o artista:', artistUsername);
      const response = await fetch('https://apicontador.sintonizy.com/status_live_artist.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          status_live: 'Ativa',
          artist_name: artistUsername,
          qtd_live: 1,
          data_hora_primeira_live: new Date().toISOString(),
          data_hora_ultima_live: new Date().toISOString(),
        }),
      });

      const responseText = await response.text();
      console.log('Resposta do servidor:', responseText);

      let data;
      try {
        data = JSON.parse(responseText);
      } catch (parseError) {
        console.error('Erro ao analisar a resposta JSON:', parseError);
        alert(`Erro na resposta do servidor. Resposta recebida: ${responseText}`);
        return;
      }

      if (!response.ok) {
        console.error('Resposta do servidor não ok:', response.status, data);
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      console.log('Live iniciada com sucesso:', data);
      setShowLiveStream(true);
      setArtistStatusLive('Ativa');
    } catch (error) {
      console.error('Erro ao iniciar a live:', error);
      if (error instanceof Error) {
        alert(`Erro ao iniciar a live: ${error.message}`);
      } else {
        alert('Erro desconhecido ao iniciar a live');
      }
    }
  };

  useEffect(() => {
    const fetchArtistStatus = async () => {
      try {
        if (!artistUsername) return;
  
        const statusResponse = await fetch(`https://apicontador.sintonizy.com/status_live_artist.php?artist_name=${artistUsername}`);
        const statusData = await statusResponse.json();
        setArtistStatusLive(statusData.status_live);
      } catch (error) {
        console.error('Erro ao buscar status do artista.', error);
      }
    };
  
    fetchArtistStatus(); // Verifique o status inicial
  
    const interval = setInterval(fetchArtistStatus, 5000); // Verifique o status a cada 5 segundos
  
    return () => clearInterval(interval); // Limpeza do intervalo ao desmontar o componente
  }, [artistUsername]);
  

 // Detecta se é um dispositivo mobile
 useEffect(() => {
  const handleResize = () => {
    setIsMobile(window.innerWidth <= 768); // Definir a largura máxima para mobile (ex: 768px)
  };

  handleResize(); // Verifica o tamanho da tela ao carregar
  window.addEventListener('resize', handleResize); // Adiciona o evento para redimensionamento

  return () => {
    window.removeEventListener('resize', handleResize); // Remove o evento ao desmontar
  };
}, []);


useEffect(() => {
    // Adicione os estilos ao cabeçalho do documento
    const style = document.createElement('style');
    style.textContent = `
      @keyframes blink {
        0%, 100% { opacity: 1; }
        50% { opacity: 0.5; }
      }
      @keyframes pulsate {
        0% { transform: scale(0.5); opacity: 0; }
        50% { opacity: 1; }
        100% { transform: scale(1.2); opacity: 0; }
      }
    `;
    document.head.appendChild(style);

    // Função de limpeza para remover o estilo quando o componente é desmontado
    return () => {
      document.head.removeChild(style);
    };
  }, []); 
  // Matriz de dependência vazia significa que este efeito é executado uma vez na montagem e a limpeza na desmontagem


  return (
    <div>
      <button
        onClick={isArtistLoggedIn ? startLive : () => setShowLiveStream(true)}
        style={{
          position: 'absolute',
          ...(isMobile
            ? {
                right: '20px',
                /*bottom: '20px', */
              }
            : {
                top: '1px',
                right: '-12px',
              }),
          padding: isMobile ? '10px 20px' : '14px 20px',
          backgroundColor: artistStatusLive === 'Ativa' ? '#ff013d' : '#222629',
          color: 'white',
          border: 'none',
          cursor: 'pointer',
          zIndex: 10000,
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          ...(isMobile ? {} : {
            borderLeft: 'solid',
            borderColor: '#4CAF50',
            borderLeftWidth: '1px',
          }),
        }}
      >
        <LiveTvIcon style={{ marginRight: '8px' }}  />
        {artistStatusLive === 'Ativa' ? (isArtistLoggedIn ? 'Iniciar Live' : 'Assistir Live') : 'Live Off-line' }
        <div style={{
          position: 'relative',
          width: '15px',
          height: '15px',
          marginLeft: '8px',
        }}>
          <span
            style={{
              position: 'absolute',
              top: '0',
              left: '0',
              width: '100%',
              height: '100%',
              borderRadius: '50%',
              backgroundColor: artistStatusLive === 'Ativa' ? '#00ff08' : '#131414',
              animation: artistStatusLive === 'Ativa' ? 'blink 1s infinite' : 'none',
              border: '2px solid #343a40',
            }}
          />
          {artistStatusLive === 'Ativa' && (
            <>
              <span
                style={{
                  position: 'absolute',
                  top: '-5px',
                  left: '-5px',
                  width: 'calc(100% + 10px)',
                  height: 'calc(100% + 10px)',
                  borderRadius: '50%',
                  backgroundColor: 'rgba(0, 255, 8, 0.3)',
                  animation: 'pulsate 2s ease-out infinite',
                }}
              />
              <span
                style={{
                  position: 'absolute',
                  top: '-10px',
                  left: '-10px',
                  width: 'calc(100% + 20px)',
                  height: 'calc(100% + 20px)',
                  borderRadius: '50%',
                  backgroundColor: 'rgba(0, 255, 8, 0.2)',
                  animation: 'pulsate 2s ease-out infinite 0.5s',
                }}
              />
            </>
          )}
        </div>
      </button>
  
      {showLiveStream && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgb(28 28 28 / 22%)',
            backdropFilter: 'blur(15px)',
            zIndex: 10000,
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
          }}
        >
          <div
            style={{
              width: isMobile ? '100%' : '70%',
              height: isMobile ? 'calc(50% - 60px)' : '100%',
              position: 'relative',
            }}
          >
            <iframe
              allow="camera; microphone; display-capture; fullscreen; clipboard-read; clipboard-write; web-share; autoplay"
              src={liveStreamUrl}
              style={{
                width: '100%',
                height: '100%',
                border: 'none',
              }}
              allowFullScreen
            ></iframe>
          </div>
  
          <div
            style={{
              width: isMobile ? '100%' : 'calc(30% - 2px)',
              height: isMobile ? 'calc(60% - 0px)' : 'calc(100% - 2px)',
              overflow: 'hidden',
              position: 'absolute',
              right: isMobile ? 0 : 2,
              bottom: isMobile ? 15 : 2,
            }}
          >
            <iframe 
              src={`https://vibe.sintonizy.com/chat/?artist_id=${artistId}`}
              style={{ width: '100%', height: '100%', border: 'none' }}
              title="Chat"
            />
          </div>
  
          <div
            style={{
              position: 'fixed',
              bottom: 0,
              left: 0,
              width: '100%',
              height: '40px',
              background: 'rgba(20, 30, 40, 0.3)',
              backdropFilter: 'blur(9px)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '0 10px',
              boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.3)',
              zIndex: 10001,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', marginLeft: '20px', color: 'white', gap: '5px', fontSize: '13px' }}>
              {userCounts ? (
                <>
                  <FaceIcon /> {userCounts.onlineCount} Usuários Online
               
                </>
              ) : (
                <span>Carregando...</span>
              )}
            </div>
  
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginRight: '20px' }}>
              <button
                onClick={toggleSharePopup}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'white',
                  cursor: 'pointer',
                  padding: '2px',
                }}
              >
                <ShareIcon />
              </button>
  
              {showSharePopup && (
                <div
                  style={{
                    position: 'absolute',
                    top: '-50px',
                    right: '90px',
                    background: 'rgba(20, 30, 40, 0.3)',
                    backdropFilter: 'blur(9px)',
                    borderRadius: '4px',
                    borderColor: '#ffffff26',
                    borderWidth: '1px',
                    boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.2)',
                    padding: '8px 20px',
                    display: 'flex',
                    gap: '22px',
                    zIndex: 10002,
                  }}
                >
                  <a href={`https://wa.me/?text=${encodeURIComponent(window.location.href)}`} target="_blank" rel="noopener noreferrer">
                    <WhatsappIcon />
                  </a>
                  <a href={`https://www.instagram.com/?url=${encodeURIComponent(window.location.href)}`} target="_blank" rel="noopener noreferrer">
                    <InstagramIcon />
                  </a>
                  <a href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`} target="_blank" rel="noopener noreferrer">
                    <FacebookIcon />
                  </a>
                  <a href={`https://t.me/share/url?url=${encodeURIComponent(window.location.href)}`} target="_blank" rel="noopener noreferrer">
                    <TelegramIcon />
                  </a>
                  <a href={`https://x.com/intent/tweet?url=${encodeURIComponent(window.location.href)}`} target="_blank" rel="noopener noreferrer">
                    <TwitterIcon />
                  </a>
                </div>
              )}
  
              <button
                onClick={toggleFullscreen}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'white',
                  cursor: 'pointer',
                  padding: '2px',
                }}
              >
                {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
              </button>
  
              <button
                onClick={isArtistLoggedIn ? closeLiveStream : closeViewerLiveStream}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'white',
                  cursor: 'pointer',
                  padding: '2px',
                }}
              >
                <CloseIcon />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}